/*
    this component was already existing in the codebase,
    we've made changes into it so we can reuse it on the new search overview page
    after we will finish the migration we can remove the old component
    and move this one to the correct location
*/

import { useCallback, useContext, useMemo } from "react";

import { partition } from "lodash";

import type { IChipDownContainerProps } from "@similarweb/ui-components";
import { ChipDownContainer, EllipsisDropdownItem } from "@similarweb/ui-components";

import { i18nFilter, translate } from "app/filters/i18nFilter";
import { SwitcherGranularityContainer } from "app/pages/website-analysis/components/SwitcherGranularityContainer";
import { allTrackers } from "app/services/track/track";

import { GraphTypeSwitcher } from "../../tabs/SearchOverviewGraph/Helpers/GraphTypeSwitcher";
import { searchOverviewContext } from "../../tabs/SearchOverviewGraph/SearchOverviewGraphContext";
import type {
    ICategory } from "../../tabs/SearchOverviewGraph/SearchOverviewGraphReducer";
import {
    isCategoryAvailableForState,
    isDataTypeAvailableForState,
} from "../../tabs/SearchOverviewGraph/SearchOverviewGraphReducer";

import {
    ButtonsContainer,
    ChipDownContainer as ChipDownWrapper,
    FiltersRowContainer,
    FiltersRowStyle,
    FiltersTitle,
    SwitchersContainer,
} from "./Components/StyledComponents";
import type {
    IChannel } from "./Helpers/SearchOverviewGraphConfig";
import {
    ALL_CHANNELS,
    availableCategories,
    availableDataTypes,
    BRANDED_NON_BRANDED_CATEGORY,
    BRANDED_NON_BRANDED_CHANNELS,
    MMX_ORGANIC_PAID_CATEGORY,
    ORGANIC_PAID_CHANNELS,
} from "./Helpers/SearchOverviewGraphConfig";

interface IUseChannelsDropdown {
    category: ICategory;
    sourceConfig: IChannel[];

    getChipdownProps?(): Partial<IChipDownContainerProps>;
}

const ChannelsDropdown = ({ category, sourceConfig, getChipdownProps }: IUseChannelsDropdown) => {
    const {
        channel,
        category: selectedCategory,
        actions: { selectChannel, resetChannelAndCategory },
        selectedMetricTab,
    } = useContext(searchOverviewContext);
    const [[selectedItem], allTheRest] = useMemo(() => {
        return partition(sourceConfig, (item) => {
            return channel !== ALL_CHANNELS && category === selectedCategory && item.id === channel;
        });
    }, [channel, sourceConfig, selectedCategory, category]);
    const onClick = useCallback(
        ({ id, cat = category }) => {
            if (id === ALL_CHANNELS) {
                return resetChannelAndCategory();
            }
            if (category !== selectedCategory || id !== channel) {
                allTrackers.dispatchTrackEvent(
                    "Graph Filter",
                    "click",
                    `Over Time Graph_Search Traffic/${category.id}/${id}`,
                );

                selectChannel({ channel: id, category: cat });
            }
        },
        [channel, category, selectedCategory],
    );

    const selectedText = selectedItem?.text;
    const buttonText = (selectedItem ?? sourceConfig[0])?.text;
    const userProps = getChipdownProps?.() ?? {};
    if (!userProps.disabled) {
        userProps.disabled = userProps.disabled || (channel !== ALL_CHANNELS && !selectedItem);
    }
    return (
        <ChipDownWrapper>
            <ChipDownContainer
                selectedIds={selectedItem ? { [selectedItem.id]: true } : {}}
                selectedText={i18nFilter()(selectedText)}
                onClick={onClick}
                onCloseItem={resetChannelAndCategory}
                buttonText={i18nFilter()(buttonText)}
                width={"auto"}
                tooltipDisabled={true}
                noResultsMessage={translate("dropdown.no.results")}
                {...userProps}
            >
                {allTheRest.map(({ id, text }) => (
                    <EllipsisDropdownItem
                        key={id}
                        id={id}
                    >
                        {i18nFilter()(text)}
                    </EllipsisDropdownItem>
                ))}
            </ChipDownContainer>
        </ChipDownWrapper>
    );
};

const MMXOrganicPaidDropdown = () => {
    const state = useContext(searchOverviewContext);
    const getChipdownProps = () => ({
        disabled: !isCategoryAvailableForState(state, MMX_ORGANIC_PAID_CATEGORY),
    });
    return (
        <ChannelsDropdown
            category={MMX_ORGANIC_PAID_CATEGORY}
            sourceConfig={ORGANIC_PAID_CHANNELS}
            getChipdownProps={getChipdownProps}
        />
    );
};

const BrandedNonBrandedDropdown = () => {
    const state = useContext(searchOverviewContext);
    const getChipdownProps = () => ({
        disabled: !isCategoryAvailableForState(state, BRANDED_NON_BRANDED_CATEGORY),
    });
    return (
        <ChannelsDropdown
            category={BRANDED_NON_BRANDED_CATEGORY}
            sourceConfig={BRANDED_NON_BRANDED_CHANNELS}
            getChipdownProps={getChipdownProps}
        />
    );
};

const useAvailableCategories = () => {
    const state = useContext(searchOverviewContext);
    const isCategoryAvailable = isCategoryAvailableForState(state);
    return availableCategories
        .map((category) => ({
            ...category,
            disabled: !isCategoryAvailable(category),
        }))
        .filter(({ disabled }) => !disabled);
};

const useAvailableDataTypes = () => {
    const state = useContext(searchOverviewContext);
    const isDataTypeAvailable = isDataTypeAvailableForState(state);
    return availableDataTypes
        .map((item) => ({
            ...item,
            disabled: !isDataTypeAvailable(item.value),
        }))
        .filter((item) => !item.disabled);
};

const CategoriesTabs = () => {
    const {
        category,
        selectedMetricTab,
        actions: { setCategory },
    } = useContext(searchOverviewContext);
    const availableCategories = useAvailableCategories();
    const selectedIndex = availableCategories.findIndex(({ id }) => category.id === id);
    const onItemClick = (index) => {
        const category = availableCategories[index];
        setCategory(category);
        allTrackers.dispatchTrackEvent("Graph Filter", "click", `Over Time Graph_Search Traffic/${category.id}`);
    };

    return (
        availableCategories.length > 1 && (
            <SwitcherGranularityContainer
                key={selectedMetricTab.name}
                itemList={availableCategories}
                selectedIndex={selectedIndex}
                onItemClick={(index) => onItemClick(index)}
                customClass={"CircleSwitcher"}
            />
        )
    );
};

const DataTypesSwitcher = () => {
    const {
        dataType,
        actions: { setDataType },
        category,
        selectedMetricTab,
    } = useContext(searchOverviewContext);
    const availableDataTypes = useAvailableDataTypes();
    const onGraphTypeSwitcherClick = (index) => {
        const type = availableDataTypes[index].value;
        allTrackers.dispatchTrackEvent("Measure Button", "click", `Over Time Graph_Search Traffic/${type}`);

        const newDataType = { ...dataType };
        newDataType[selectedMetricTab.name][category.id] = type;
        setDataType(newDataType);
    };
    return (
        availableDataTypes.length > 1 && (
            <SwitchersContainer>
                <GraphTypeSwitcher
                    onItemClick={(index) => onGraphTypeSwitcherClick(index)}
                    selectedIndex={availableDataTypes.findIndex(
                        (item) => item.value === dataType[selectedMetricTab.name][category.id],
                    )}
                    buttonsList={availableDataTypes}
                />
            </SwitchersContainer>
        )
    );
};

export const FiltersRow = () => {
    const context = useContext(searchOverviewContext);
    const {
        emptyState,
        isSingle,
        isTotal,
        isDesktop,
        granularity,
        actions: { setTimeGranularity },
    } = context;

    // in the overview page we always want to show the monthly granularity
    if (String(granularity) !== "monthly") {
        setTimeGranularity("monthly");
    }

    return (
        <FiltersRowContainer>
            <FiltersRowStyle data-automation="search-tab-graph-filters">
                <FiltersTitle>{i18nFilter()("search.overview.graph.filters.row.title")}</FiltersTitle>
                {!isSingle ? (
                    <>
                        <MMXOrganicPaidDropdown />
                        <BrandedNonBrandedDropdown />
                    </>
                ) : null}
                {isSingle ? <CategoriesTabs /> : null}
            </FiltersRowStyle>
            {!emptyState ? (
                <ButtonsContainer data-automation="search-tab-graph-butttons">
                    <DataTypesSwitcher />
                </ButtonsContainer>
            ) : null}
        </FiltersRowContainer>
    );
};
